import { AlertTriangle, Check, FileText, Send, Target, TrendingUp } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { CampaignDetail } from '@/lib/schemas';

/**
 * The campaign flow, as a step nav (spec § 4.1).
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────────────
 *
 * Every screen it links to already existed and worked. What did not exist was any route between
 * them: the workspace's only link went back to the campaigns list, and `/send` and `/report` were
 * reachable only by typing the URL. Four working screens with no path between them is not a product,
 * and the fix was navigation rather than features.
 *
 * ── IT SHOWS COMPLETION, NOT JUST POSITION ────────────────────────────────────────
 *
 * A tick on Audience means an audience is set. That is read from the campaign itself rather than from
 * the checklist, deliberately: the checklist is the authority on whether a send may proceed, and
 * duplicating its judgement here would give two answers to one question. This is only "have you done
 * this bit yet", which is the question somebody halfway through building a campaign is asking.
 *
 * ── ORDER IS INFORMATION, NOT DECORATION ──────────────────────────────────────────
 *
 * The steps are numbered because they genuinely are a sequence — you cannot usefully review a send
 * whose audience is unset, and the checklist blocks exactly that. But they are LINKS, not a wizard:
 * skipping ahead to look and coming back is normal, and a wizard that forbids it would be fighting
 * the user for no benefit.
 *
 * ── THE ROUTER DECIDES WHICH STEP IS CURRENT ──────────────────────────────────────
 *
 * There is no `current` prop. An earlier version took one, which meant the answer to "which step am
 * I on" came from both the caller AND `NavLink`'s own route matching — and they disagreed, because
 * NavLink overwrites `aria-current` from the route regardless of what the caller passed. One source
 * of truth, and it is the URL, which is the thing that is actually true.
 *
 * `aria-current="step"` rather than NavLink's default of `"page"`: this is a position in a process,
 * and that is the value ARIA defines for it.
 */

type StepId = 'content' | 'audience' | 'send' | 'report';

type StepDefinition = {
  id: StepId;
  label: string;
  to: (campaignId: string) => string;
  Icon: typeof FileText;
  /** Whether this step's own prerequisite is satisfied. */
  isComplete: (campaign: CampaignDetail) => boolean;
  /** Report only becomes meaningful once something has been sent. */
  isAvailable?: (campaign: CampaignDetail) => boolean;
};

const STEPS: StepDefinition[] = [
  {
    id: 'content',
    label: 'Content',
    to: (id) => `/campaigns/${id}`,
    Icon: FileText,
    // A subject is set at creation, so content is "done" once there is a body worth sending. The
    // linter judges quality; this only asks whether the editor is empty.
    isComplete: (campaign) => campaign.arms.some((arm) => arm.htmlAuthored.trim().length > 0),
  },
  {
    id: 'audience',
    label: 'Audience',
    to: (id) => `/campaigns/${id}/audience`,
    Icon: Target,
    isComplete: (campaign) => campaign.segmentId !== null,
  },
  {
    id: 'send',
    label: 'Review & send',
    to: (id) => `/campaigns/${id}/send`,
    Icon: Send,
    isComplete: (campaign) => campaign.sentAt !== null,
  },
  {
    id: 'report',
    label: 'Report',
    to: (id) => `/campaigns/${id}/report`,
    Icon: TrendingUp,
    isComplete: () => false,
    // Shown but disabled before a send: hiding it entirely would make the flow look like it ends at
    // "send", and the report is where anybody returns afterwards.
    isAvailable: (campaign) => campaign.sentAt !== null || campaign.status === 'Sending',
  },
];

const SHARED =
  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13.5px] font-medium transition-colors';

/** The marker and label. Split out because both the link and the disabled span render it. */
function StepBody({
  index,
  label,
  isComplete,
  isCurrent,
}: {
  index: number;
  label: string;
  isComplete: boolean;
  isCurrent: boolean;
}) {
  return (
    <>
      <span
        className={cn(
          'grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold',
          isComplete
            ? 'bg-success-bg text-success-ink'
            : isCurrent
              ? 'bg-primary-fill text-primary-on-fill'
              : 'bg-surface-2 text-muted',
        )}
        aria-hidden="true"
      >
        {isComplete ? <Check className="size-3" /> : index + 1}
      </span>

      <span>{label}</span>

      {/* The tick is aria-hidden, so this is the signal a screen reader actually receives. Colour is
          never the only one (ui-ux.md § 7). */}
      {isComplete && <span className="sr-only">(done)</span>}
    </>
  );
}

export function CampaignSteps({ campaign }: { campaign: CampaignDetail }) {
  return (
    <nav aria-label="Campaign steps" className="flex flex-wrap items-center gap-1">
      {STEPS.map((step, index) => {
        const isAvailable = step.isAvailable?.(campaign) ?? true;
        const isComplete = step.isComplete(campaign);

        if (!isAvailable) {
          return (
            <span
              key={step.id}
              className={cn(SHARED, 'cursor-not-allowed text-muted')}
              title="Available once the campaign has been sent"
            >
              <StepBody index={index} label={step.label} isComplete={false} isCurrent={false} />
            </span>
          );
        }

        return (
          <NavLink
            key={step.id}
            to={step.to(campaign.id)}
            // Without `end`, /campaigns/:id matches every step beneath it and Content stays lit.
            end={step.id === 'content'}
            aria-current="step"
            className={({ isActive }) =>
              cn(
                SHARED,
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
                isActive
                  ? 'bg-primary-subtle text-primary-strong'
                  : 'text-neutral-strong hover:bg-row-hover hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <StepBody
                index={index}
                label={step.label}
                isComplete={isComplete}
                isCurrent={isActive}
              />
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * The one-line answer to "what is stopping this going out".
 *
 * Shown beside the steps on every screen in the flow, because the most common question at any point
 * in building a campaign is that one — and making somebody navigate to the review step to find out
 * is how they end up discovering a blocker after writing the whole email.
 */
export function CampaignBlockers({
  blockingCount,
  isLoading,
}: {
  blockingCount: number | undefined;
  isLoading: boolean;
}) {
  // Nothing during load rather than a spinner: this sits in a header that is already showing the
  // campaign's name and status, and a flickering badge beside them reads as instability.
  if (isLoading || blockingCount === undefined) return null;

  if (blockingCount === 0) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-success-ink">
        <Check className="size-3.5" aria-hidden="true" />
        Ready to send
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-danger-ink">
      <AlertTriangle className="size-3.5" aria-hidden="true" />
      {blockingCount} {blockingCount === 1 ? 'thing' : 'things'} to fix before sending
    </span>
  );
}
